import { useRouter } from 'expo-router';
import { type ReactNode, useEffect, useState } from 'react';
import { Pressable, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import type { MissedEarnings } from '@/api/partners/dashboard/use-dashboard-performance';
import {
  DASHBOARD_PERIOD_TIME_ZONE,
  getPerformancePeriod,
  performanceChartEarningsMonth,
  useDashboardPerformance,
} from '@/api/partners/dashboard/use-dashboard-performance';
import { formatCurrency } from '@/core';
import { useAnimateWhenInView } from '@/core/hooks/use-animate-when-in-view';
import { Card, Text } from '@/ui';
import { DonutChart } from '@/ui/donut-chart';
import { Icon } from '@/ui/icon';
import { Car } from '@/ui/icons';
import { ArrowDown } from '@/ui/icons/arrow-down';
import { ArrowUp } from '@/ui/icons/arrow-up';
import { Calendar as CalendarIcon } from '@/ui/icons/calendar';
import { Loader } from '@/ui/loader';
import { StackedBarChart } from '@/ui/stacked-bar-chart';

import {
  DASHBOARD_EARNED_SERIES_KEYS,
  dashboardEarningsSeries,
  dashboardShareBarColorsFromSeries,
} from './dashboard-chart-colors';
import {
  formatChartMonth,
  formatChartMonthWithYear,
  formatDashboardTripDate,
  formatMissedEarningsCarLabel,
  integerSharePercents,
} from './dashboard-formatters';

const MISSED_TRIPS_PREVIEW_COUNT = 3;
const MISSED_TRIPS_INITIAL_COUNT = 3;
const MISSED_TRIPS_PAGE_SIZE = 10;
const PARTNER_CARS_ROUTE = '/partner/cars' as const;
const PARTNER_RESERVATIONS_ROUTE = '/partner/reservations' as const;

type FleetStat = {
  label: string;
  value: number;
  icon: ReactNode;
  href?: typeof PARTNER_CARS_ROUTE | typeof PARTNER_RESERVATIONS_ROUTE;
};

type PerformanceViewProps = {
  metrics?: {
    totalCarsCount?: number;
    totalActiveCarsCount?: number;
    ongoingRentalsCount?: number;
    upcomingRentalsCount?: number;
  };
  monthCount: number;
  carId?: number;
};

function formatCents(cents: number) {
  return formatCurrency(cents / 100);
}

function formatAxisCents(cents: number) {
  const dollars = cents / 100;
  if (Math.abs(dollars) >= 1000) {
    return `$${Math.round(dollars / 1000)}K`;
  }

  return formatCurrency(dollars);
}

function formatStackedBarAxisCents(cents: number) {
  const dollars = cents / 100;

  if (dollars === 0) {
    return '0';
  }

  if (Math.abs(dollars) >= 1000) {
    return `${Math.round(dollars / 1000)}K`;
  }

  return String(Math.round(dollars));
}

function roundCancellationRatePercent(percent: number) {
  return Math.round(percent * 10) / 10;
}

function formatCancellationRatePercent(percent: number) {
  return `${roundCancellationRatePercent(percent).toFixed(1)}%`;
}

function computedCancellationRatePercent(
  tripCount: number,
  bookedTripCount: number
) {
  if (bookedTripCount === 0) {
    return null;
  }

  return roundCancellationRatePercent((tripCount / bookedTripCount) * 100);
}

function formatCancellationRateDisplay(missedEarnings: MissedEarnings) {
  if (missedEarnings.cancellationRate != null) {
    return formatCancellationRatePercent(missedEarnings.cancellationRate);
  }

  const computed = computedCancellationRatePercent(
    missedEarnings.tripCount,
    missedEarnings.bookedTripCount
  );

  return computed === null ? '—' : formatCancellationRatePercent(computed);
}

function canceledTripsLabel(count: number) {
  return count === 1 ? '1 canceled trip' : `${count} canceled trips`;
}

function previousPeriodLabel(monthCount: number) {
  return monthCount === 1
    ? 'vs previous month'
    : `vs previous ${monthCount} months`;
}

function formatCancellationRateChange({
  missedEarnings,
  monthCount,
}: {
  missedEarnings: MissedEarnings;
  monthCount: number;
}) {
  if (missedEarnings.showCancellationRateComparison !== true) {
    return null;
  }

  const currentRate =
    missedEarnings.cancellationRate ??
    computedCancellationRatePercent(
      missedEarnings.tripCount,
      missedEarnings.bookedTripCount
    );
  const previousRate = missedEarnings.previousCancellationRate;

  if (currentRate === null || previousRate === null) {
    return null;
  }

  const delta = currentRate - previousRate;
  const magnitude = Math.abs(delta);

  if (magnitude < 0.05) {
    return null;
  }

  const formatted = roundCancellationRatePercent(magnitude).toFixed(1);

  return {
    magnitude: `${formatted}%`,
    periodLabel: previousPeriodLabel(monthCount),
    improved: delta < 0,
  };
}

function missedTripCountForMonth(
  trips: MissedEarnings['trips'],
  monthKey: string
) {
  return trips.filter((trip) => trip.tripDate.startsWith(monthKey)).length;
}

export function PerformanceView({
  metrics,
  monthCount,
  carId,
}: PerformanceViewProps) {
  const { styles, theme } = useStyles(stylesheet);
  const earningsSeries = dashboardEarningsSeries(theme.colors.error);
  const shareBarColors = dashboardShareBarColorsFromSeries(earningsSeries);
  const router = useRouter();
  const period = getPerformancePeriod(monthCount);
  const performance = useDashboardPerformance({
    variables: { from: period.from, to: period.to, carId },
  });

  const data = performance.data;
  const periodTimeZone = data?.period.timeZone ?? DASHBOARD_PERIOD_TIME_ZONE;
  const chartEarningsByMonth =
    data?.earningsByMonth?.map(performanceChartEarningsMonth) ?? [];

  const dailyTotal = chartEarningsByMonth.reduce(
    (sum, month) => sum + month.dailyRentalsCents,
    0
  );
  const subscriptionTotal = chartEarningsByMonth.reduce(
    (sum, month) => sum + month.subscriptionsCents,
    0
  );
  const deliveryTotal = chartEarningsByMonth.reduce(
    (sum, month) => sum + month.deliveryCents,
    0
  );
  const totalEarnings = dailyTotal + subscriptionTotal + deliveryTotal;
  const donutTotal = dailyTotal + subscriptionTotal + deliveryTotal;
  const earningsByTypeSeries = earningsSeries.slice(0, 3);
  const earningsByTypeValues = earningsByTypeSeries.map((item) => {
    if (item.key === 'dailyRentalsCents') {
      return dailyTotal;
    }

    if (item.key === 'subscriptionsCents') {
      return subscriptionTotal;
    }

    return deliveryTotal;
  });
  const earningsByTypePercents = integerSharePercents(earningsByTypeValues);
  const rentalCount =
    (data?.completedDailyRentalCount ?? 0) +
    (data?.completedSubscriptionTripCount ?? 0);
  const missedEarnings = data?.missedEarnings;
  const hasMissedEarnings =
    (missedEarnings?.tripCount ?? 0) > 0 ||
    (missedEarnings?.totalCents ?? 0) > 0;
  const hasEarnings = totalEarnings > 0 || rentalCount > 0 || hasMissedEarnings;

  const isCompactLayout = ['xs', 'sm'].includes(UnistylesRuntime.breakpoint);
  const stats: FleetStat[] = [
    {
      label: 'Total fleet',
      value: metrics?.totalCarsCount ?? 0,
      icon: <Car />,
      href: PARTNER_CARS_ROUTE,
    },
    {
      label: 'Listed cars',
      value: metrics?.totalActiveCarsCount ?? 0,
      icon: <Car />,
      href: PARTNER_CARS_ROUTE,
    },
    {
      label: 'Active trips',
      value: metrics?.ongoingRentalsCount ?? 0,
      icon: <CalendarIcon />,
      href: PARTNER_RESERVATIONS_ROUTE,
    },
    {
      label: 'Upcoming trips',
      value: metrics?.upcomingRentalsCount ?? 0,
      icon: <CalendarIcon />,
      href: PARTNER_RESERVATIONS_ROUTE,
    },
  ];

  const fleetStatWrapper = (
    stat: FleetStat,
    style: object,
    children: ReactNode
  ) => {
    const href = stat.href;

    if (!href) {
      return (
        <View key={stat.label} style={style}>
          {children}
        </View>
      );
    }

    return (
      <Pressable
        key={stat.label}
        style={style}
        onPress={() => {
          router.replace(href);
        }}
      >
        {children}
      </Pressable>
    );
  };

  return (
    <View style={styles.container}>
      {isCompactLayout ? (
        <View style={styles.fleetSection}>
          <Text type="bodyBold">Fleet</Text>
          <Card style={styles.fleetCard}>
            <View style={styles.fleetGrid}>
              {stats.map((stat) =>
                fleetStatWrapper(
                  stat,
                  styles.fleetMetric,
                  <>
                    <Text type="caption1" style={styles.secondary}>
                      {stat.label}
                    </Text>
                    <Text type="title1">{stat.value}</Text>
                  </>
                )
              )}
            </View>
          </Card>
        </View>
      ) : (
        <View style={styles.stats}>
          {stats.map((stat) =>
            fleetStatWrapper(
              stat,
              styles.statPressable,
              <Card style={styles.stat}>
                <View style={styles.statCopy}>
                  <Text type="caption1" style={styles.secondary}>
                    {stat.label}
                  </Text>
                  <Text type="title1">{stat.value}</Text>
                </View>
                <Icon
                  type="dressed"
                  icon={stat.icon}
                  width={20}
                  height={20}
                  fillColor={theme.colors.icon.color}
                />
              </Card>
            )
          )}
        </View>
      )}

      <Text type="bodyBold">Financial performance</Text>

      {performance.isLoading ? (
        <Loader style={styles.loader} />
      ) : !data || !hasEarnings ? (
        <Card style={styles.empty}>
          <Text type="title2">No Earnings Yet</Text>
          <Text type="bodyRegular" style={styles.secondary}>
            Earnings appear here after your first completed trip.
          </Text>
        </Card>
      ) : (
        <>
          <Card style={styles.card}>
            <Text type="bodyBold">Earnings</Text>
            <View>
              <Text type="caption1" style={styles.secondary}>
                Total earnings
              </Text>
              <Text type="largeTitle">{formatCents(totalEarnings)}</Text>
            </View>
            <View style={[styles.legend, styles.earningsLegend]}>
              {earningsSeries.map((item) => (
                <View key={item.key} style={styles.legendItem}>
                  <View
                    style={[styles.swatch, { backgroundColor: item.color }]}
                  />
                  <Text type="caption1" style={styles.secondary}>
                    {item.label}
                  </Text>
                </View>
              ))}
            </View>
            <StackedBarChart
              bars={(data.earningsByMonth ?? []).map((month) => {
                const chartMonth = performanceChartEarningsMonth(month);
                const missedTripCount = missedTripCountForMonth(
                  missedEarnings?.trips ?? [],
                  month.month
                );

                return {
                  label: formatChartMonth(month.month),
                  detailLabel: isCompactLayout
                    ? formatChartMonthWithYear(month.month)
                    : undefined,
                  values: {
                    dailyRentalsCents: chartMonth.dailyRentalsCents,
                    subscriptionsCents: chartMonth.subscriptionsCents,
                    deliveryCents: chartMonth.deliveryCents,
                    missedEarningsCents: chartMonth.missedEarningsCents,
                  },
                  labelCaptions:
                    missedTripCount > 0
                      ? {
                          missedEarningsCents:
                            canceledTripsLabel(missedTripCount),
                        }
                      : undefined,
                };
              })}
              series={earningsSeries}
              formatValue={formatCents}
              formatAxis={
                isCompactLayout ? formatStackedBarAxisCents : formatAxisCents
              }
              totalSuffix=" earned"
              totalSeriesKeys={DASHBOARD_EARNED_SERIES_KEYS}
            />
          </Card>

          <View style={styles.breakdownRow}>
            <Card style={[styles.card, styles.breakdownCard]}>
              <Text type="bodyBold">Earnings by type</Text>
              <View style={styles.donutBody}>
                <View style={styles.donutRow}>
                  <DonutChart
                    segments={earningsByTypeSeries.map((item, index) => ({
                      key: item.key,
                      color: item.color,
                      value: earningsByTypeValues[index],
                    }))}
                    centerValue={formatCents(donutTotal)}
                    centerCaption="Total earnings"
                  />
                  <View style={styles.donutLegend}>
                    {earningsByTypeSeries.map((item, index, items) => {
                      const value = earningsByTypeValues[index];
                      const percent = earningsByTypePercents[index];

                      return (
                        <View
                          key={item.key}
                          style={[
                            styles.donutLegendRow,
                            index < items.length - 1
                              ? styles.donutLegendRowDivider
                              : undefined,
                          ]}
                        >
                          <View style={styles.legendItem}>
                            <View
                              style={[
                                styles.dot,
                                { backgroundColor: item.color },
                              ]}
                            />
                            <Text type="bodyRegular">{item.label}</Text>
                          </View>
                          <View style={styles.donutLegendValues}>
                            <Text type="bodyRegular">{formatCents(value)}</Text>
                            <Text type="bodyRegular">
                              {value > 0 && value / donutTotal < 0.01
                                ? '<1%'
                                : `${percent}%`}
                            </Text>
                          </View>
                        </View>
                      );
                    })}
                  </View>
                </View>
              </View>
            </Card>

            <Card style={[styles.card, styles.breakdownCard]}>
              <Text type="bodyBold">Daily rentals vs subscriptions</Text>
              <View
                style={
                  isCompactLayout
                    ? styles.splitCardBody
                    : styles.splitCardContent
                }
              >
                <View style={styles.legend}>
                  <View style={styles.legendItem}>
                    <View
                      style={[
                        styles.swatch,
                        { backgroundColor: shareBarColors.daily },
                      ]}
                    />
                    <Text type="caption1" style={styles.secondary}>
                      Daily rentals
                    </Text>
                  </View>
                  <View style={styles.legendItem}>
                    <View
                      style={[
                        styles.swatch,
                        { backgroundColor: shareBarColors.subscriptions },
                      ]}
                    />
                    <Text type="caption1" style={styles.secondary}>
                      Subscriptions
                    </Text>
                  </View>
                </View>
                <ShareBar
                  label="Share of rentals"
                  leftValue={data.completedDailyRentalCount}
                  rightValue={data.completedSubscriptionTripCount}
                  formatValue={(value) => String(value)}
                  deferAnimation={isCompactLayout}
                  leftColor={shareBarColors.daily}
                  rightColor={shareBarColors.subscriptions}
                />
                <ShareBar
                  label="Share of rental earnings"
                  leftValue={dailyTotal}
                  rightValue={subscriptionTotal}
                  formatValue={formatCents}
                  deferAnimation={isCompactLayout}
                  leftColor={shareBarColors.daily}
                  rightColor={shareBarColors.subscriptions}
                />
              </View>
            </Card>
          </View>

          {missedEarnings ? (
            <MissedEarningsCard
              key={`${monthCount}-${carId ?? 'all'}-${
                isCompactLayout ? 'compact' : 'regular'
              }`}
              isCompactLayout={isCompactLayout}
              missedEarnings={missedEarnings}
              monthCount={monthCount}
              periodTimeZone={periodTimeZone}
              onOpenReservation={(reservationId) => {
                router.push(`/partner/reservations/${reservationId}`);
              }}
            />
          ) : null}
        </>
      )}
    </View>
  );
}

function MissedEarningsCard({
  isCompactLayout,
  missedEarnings,
  monthCount,
  periodTimeZone,
  onOpenReservation,
}: {
  isCompactLayout: boolean;
  missedEarnings: MissedEarnings;
  monthCount: number;
  periodTimeZone: string;
  onOpenReservation: (reservationId: number) => void;
}) {
  const { styles, theme } = useStyles(stylesheet);
  const [showAllTrips, setShowAllTrips] = useState(false);
  const [visibleTripCount, setVisibleTripCount] = useState(
    MISSED_TRIPS_INITIAL_COUNT
  );
  const { tripCount, bookedTripCount, totalCents, trips } = missedEarnings;
  const rateChange = formatCancellationRateChange({
    missedEarnings,
    monthCount,
  });
  const hiddenTripCount = Math.max(
    0,
    trips.length - MISSED_TRIPS_PREVIEW_COUNT
  );
  const desktopVisibleTrips = showAllTrips
    ? trips
    : trips.slice(0, MISSED_TRIPS_PREVIEW_COUNT);
  const mobileVisibleTrips = trips.slice(0, visibleTripCount);
  const remainingTripCount = Math.max(0, trips.length - visibleTripCount);

  const rateChangeLabel = rateChange ? (
    <View style={styles.missedChange}>
      <View style={styles.missedChangeIcon}>
        {rateChange.improved ? (
          <ArrowDown width={10} height={12} color={theme.colors.success} />
        ) : (
          <ArrowUp width={10} height={12} color={theme.colors.error} />
        )}
      </View>
      <Text type="caption1">
        <Text
          type={isCompactLayout ? 'caption1Bold' : 'caption1'}
          style={
            rateChange.improved
              ? styles.missedChangeGood
              : styles.missedChangeBad
          }
        >
          {rateChange.magnitude}
        </Text>
        <Text type="caption1" style={styles.secondary}>
          {` ${rateChange.periodLabel}`}
        </Text>
      </Text>
    </View>
  ) : null;

  return (
    <Card
      style={[
        styles.card,
        isCompactLayout ? styles.missedCardCompact : undefined,
      ]}
    >
      <Text type={isCompactLayout ? 'title2' : 'bodyBold'}>
        {isCompactLayout ? 'Missed earnings' : 'Missed Earnings'}
      </Text>
      <View
        style={
          isCompactLayout ? styles.missedMetricsCompact : styles.missedMetrics
        }
      >
        <View
          style={
            isCompactLayout ? styles.missedMetricCompact : styles.missedMetric
          }
        >
          <Text type="caption1" style={styles.secondary}>
            Cancellation rate
          </Text>
          <Text type={isCompactLayout ? 'largeTitle' : 'title1'}>
            {formatCancellationRateDisplay(missedEarnings)}
          </Text>
          {rateChangeLabel}
        </View>
        <View
          style={
            isCompactLayout ? styles.missedMetricCompact : styles.missedMetric
          }
        >
          <Text type="caption1" style={styles.secondary}>
            Canceled trips
          </Text>
          <Text type={isCompactLayout ? 'largeTitle' : 'title1'}>
            {tripCount}
          </Text>
          <Text type="caption1" style={styles.secondary}>
            {bookedTripCount > 0
              ? `of ${bookedTripCount.toLocaleString()} booked trips`
              : 'No booked trips in period'}
          </Text>
        </View>
        <View
          style={
            isCompactLayout ? styles.missedMetricCompact : styles.missedMetric
          }
        >
          <Text type="caption1" style={styles.secondary}>
            Missed earnings
          </Text>
          <Text type={isCompactLayout ? 'largeTitle' : 'title1'}>
            {formatCents(totalCents)}
          </Text>
          <Text type="caption1" style={styles.secondary}>
            from canceled trips
          </Text>
        </View>
      </View>

      {trips.length > 0 && isCompactLayout ? (
        <View style={styles.missedTripsList}>
          {mobileVisibleTrips.map((trip, index) => (
            <View
              key={trip.reservationId}
              style={[
                styles.missedTripRow,
                index < mobileVisibleTrips.length - 1 &&
                  styles.missedTripRowBorder,
              ]}
            >
              <View style={styles.missedTripTop}>
                <Pressable
                  style={styles.missedReservationLink}
                  onPress={() => onOpenReservation(trip.reservationId)}
                >
                  <Text type="caption1" style={styles.missedLink}>
                    {`Reservation ${trip.reservationId}`}
                  </Text>
                </Pressable>
                <Text type="caption1" style={styles.missedTripAmount}>
                  {formatCents(trip.missedEarningsCents)}
                </Text>
              </View>
              <View style={styles.missedTripBottom}>
                <Text
                  type="caption1"
                  style={[styles.missedTripDate, styles.secondary]}
                  numberOfLines={1}
                >
                  {formatDashboardTripDate(trip.tripDate, periodTimeZone)}
                </Text>
                <Text
                  type="caption1"
                  style={[styles.missedTripCar, styles.secondary]}
                  numberOfLines={1}
                >
                  {formatMissedEarningsCarLabel(trip)}
                </Text>
              </View>
            </View>
          ))}
          {remainingTripCount > 0 ? (
            <Pressable
              style={styles.missedShowMoreCompact}
              onPress={() =>
                setVisibleTripCount((count) =>
                  Math.min(trips.length, count + MISSED_TRIPS_PAGE_SIZE)
                )
              }
            >
              <Text type="caption1" style={styles.missedLink}>
                {`Show ${Math.min(
                  MISSED_TRIPS_PAGE_SIZE,
                  remainingTripCount
                )} more`}
              </Text>
            </Pressable>
          ) : null}
        </View>
      ) : null}

      {trips.length > 0 && !isCompactLayout ? (
        <View style={styles.missedTable}>
          {desktopVisibleTrips.map((trip) => (
            <View key={trip.reservationId} style={styles.missedTableRow}>
              <Pressable
                style={styles.missedReservation}
                onPress={() => onOpenReservation(trip.reservationId)}
              >
                <Text type="caption1" style={styles.missedLink}>
                  {`Reservation ${trip.reservationId}`}
                </Text>
              </Pressable>
              <Text
                type="caption1"
                style={[styles.missedDate, styles.secondary]}
                numberOfLines={1}
              >
                {formatDashboardTripDate(trip.tripDate, periodTimeZone)}
              </Text>
              <Text
                type="caption1"
                style={[styles.missedCar, styles.secondary]}
                numberOfLines={1}
              >
                {formatMissedEarningsCarLabel(trip)}
              </Text>
              <Text type="caption1" style={styles.missedAmount}>
                {formatCents(trip.missedEarningsCents)}
              </Text>
            </View>
          ))}
          {hiddenTripCount > 0 && !showAllTrips ? (
            <Pressable
              style={styles.missedShowMore}
              onPress={() => setShowAllTrips(true)}
            >
              <Text type="bodyRegular" style={styles.missedLink}>
                {`Show ${hiddenTripCount} more`}
              </Text>
            </Pressable>
          ) : null}
        </View>
      ) : null}
    </Card>
  );
}

function ShareBar({
  label,
  leftValue,
  rightValue,
  formatValue,
  deferAnimation,
  leftColor,
  rightColor,
}: {
  label: string;
  leftValue: number;
  rightValue: number;
  formatValue: (value: number) => string;
  deferAnimation?: boolean;
  leftColor: string;
  rightColor: string;
}) {
  const { styles } = useStyles(stylesheet);
  const total = leftValue + rightValue;
  const leftPercent = total === 0 ? 0 : Math.round((leftValue / total) * 100);
  const rightPercent = total === 0 ? 0 : 100 - leftPercent;
  const { ref, isInView, onLayout } = useAnimateWhenInView();
  const progress = useSharedValue(total === 0 ? 1 : 0);
  const shouldAnimate = !deferAnimation || isInView;

  useEffect(() => {
    if (total === 0) {
      progress.value = 1;
      return;
    }

    if (!shouldAnimate) {
      progress.value = 0;
      return;
    }

    progress.value = 0;
    progress.value = withTiming(1, {
      duration: 700,
      easing: Easing.out(Easing.cubic),
    });
  }, [shouldAnimate, leftPercent, progress, rightPercent, total]);

  const animatedLeftStyle = useAnimatedStyle(() => ({
    width: `${100 * (1 - progress.value) + leftPercent * progress.value}%`,
  }));

  const animatedRightStyle = useAnimatedStyle(() => ({
    width: `${rightPercent * progress.value}%`,
  }));

  return (
    <View
      ref={deferAnimation ? ref : undefined}
      onLayout={deferAnimation ? onLayout : undefined}
      style={styles.share}
    >
      <Text type="caption1" style={styles.secondary}>
        {label}
      </Text>
      <View style={styles.shareValues}>
        <Text type="title2">{`${leftPercent}%`}</Text>
        <Text type="title2">{`${rightPercent}%`}</Text>
      </View>
      <View style={styles.shareTrack}>
        {total === 0 ? null : (
          <>
            <Animated.View
              style={[
                styles.shareSegment,
                { backgroundColor: leftColor },
                animatedLeftStyle,
              ]}
            />
            <Animated.View
              style={[
                styles.shareSegment,
                { backgroundColor: rightColor },
                animatedRightStyle,
              ]}
            />
          </>
        )}
      </View>
      <View style={styles.shareValues}>
        <Text type="caption1" style={styles.secondary}>
          {formatValue(leftValue)}
        </Text>
        <Text type="caption1" style={styles.secondary}>
          {formatValue(rightValue)}
        </Text>
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    gap: 24,
  },
  fleetSection: {
    gap: 16,
  },
  fleetCard: {
    padding: 16,
  },
  fleetGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    rowGap: 24,
    columnGap: 16,
  },
  fleetMetric: {
    width: '47%',
    gap: 4,
  },
  stats: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  statPressable: {
    flexGrow: 1,
    flexBasis: 160,
  },
  stat: {
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  statCopy: {
    gap: 4,
  },
  secondary: {
    color: theme.colors.text.secondary,
  },
  card: {
    padding: 16,
    gap: 16,
  },
  legend: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  earningsLegend: {
    marginBottom: 60,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  swatch: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  dot: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  breakdownRow: {
    flexDirection: 'row',
    flexWrap: {
      xs: 'wrap',
      md: 'nowrap',
    },
    alignItems: 'stretch',
    gap: 16,
    width: '100%',
  },
  breakdownCard: {
    flex: 1,
    minWidth: {
      xs: '100%',
      md: 0,
    },
    minHeight: {
      md: 320,
    },
  },
  splitCardBody: {
    gap: 16,
    flex: {
      md: 1,
    },
    justifyContent: {
      md: 'center',
    },
  },
  splitCardContent: {
    gap: 16,
  },
  donutBody: {
    gap: 16,
    flex: {
      md: 1,
    },
    justifyContent: {
      md: 'center',
    },
  },
  missedCardCompact: {
    gap: 24,
  },
  missedMetrics: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    columnGap: 64,
  },
  missedMetricsCompact: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    rowGap: 16,
    columnGap: 32,
  },
  missedMetric: {
    flex: 1,
    flexBasis: 0,
    minWidth: 0,
    gap: 4,
  },
  missedMetricCompact: {
    flex: 1,
    flexBasis: '47%',
    minWidth: 0,
    gap: 8,
  },
  missedChange: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  missedChangeIcon: {
    width: 10,
    height: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: -2,
  },
  missedChangeGood: {
    color: theme.colors.success,
  },
  missedChangeBad: {
    color: theme.colors.error,
  },
  missedLink: {
    textDecorationLine: 'underline',
  },
  missedTripsList: {
    borderWidth: 1,
    borderRadius: 8,
    borderColor: theme.colors.card.hover.borderColor,
    backgroundColor: theme.colors.card.hover.backgroundColor,
    overflow: 'hidden',
  },
  missedTripRow: {
    padding: 8,
    gap: 4,
  },
  missedTripRowBorder: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.card.default.borderColor,
  },
  missedTripTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  missedTripBottom: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  missedTripCar: {
    flex: 1,
    minWidth: 0,
  },
  missedShowMore: {
    paddingTop: 8,
  },
  missedShowMoreCompact: {
    paddingHorizontal: 8,
    paddingBottom: 8,
  },
  missedTable: {
    gap: 0,
  },
  missedTableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.card.default.borderColor,
  },
  missedReservation: {
    flexShrink: 0,
  },
  missedReservationLink: {
    flexShrink: 0,
  },
  missedDate: {
    flexShrink: 0,
  },
  missedCar: {
    flex: 1,
    minWidth: 0,
  },
  missedAmount: {
    flexShrink: 0,
    textAlign: 'right',
  },
  missedTripDate: {
    flexShrink: 0,
  },
  missedTripAmount: {
    flexShrink: 0,
  },
  donutRow: {
    width: '100%',
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 24,
  },
  donutLegend: {
    width: '100%',
    minWidth: 0,
    alignSelf: 'stretch',
    flex: {
      md: 1,
    },
    justifyContent: {
      md: 'center',
    },
  },
  donutLegendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    gap: 16,
  },
  donutLegendRowDivider: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.card.default.borderColor,
  },
  donutLegendValues: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  share: {
    gap: 8,
  },
  shareValues: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  shareTrack: {
    flexDirection: 'row',
    height: 8,
    borderRadius: 4,
    overflow: 'hidden',
    backgroundColor: theme.colors.card.default.borderColor,
  },
  shareSegment: {
    height: '100%',
  },
  empty: {
    padding: 24,
    gap: 8,
  },
  loader: {
    height: 180,
  },
}));
